import { createStore, get, set, del, keys } from 'idb-keyval';

const DB_NAME = 'halalin-ta-offline';
const STORE_NAME = 'snapshots';

const customStore = createStore(DB_NAME, STORE_NAME);

/** Prefix: halalin:ta-offline:v1:${userId}:${snapshotName} (snapshotName is the logical key) */
const NS = 'halalin:ta-offline:v1';

export const TA_OFFLINE_SNAPSHOT_VERSION = 1;

const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * @param {string|number} userId
 * @param {string} snapshotName e.g. dashboard, my-tasks, attendance:from:to, clients
 */
function storageKey(userId, snapshotName) {
  return `${NS}:${String(userId)}:${snapshotName}`;
}

function isExpired(record) {
  if (!record || record.expiresAt == null) return true;
  return Date.now() > record.expiresAt;
}

/**
 * @param {string|number} userId
 * @param {string} snapshotName
 * @returns {Promise<null|{ userId: string, savedAt: number, expiresAt: number, schemaVersion: number, data: unknown }>}
 */
export async function getTASnapshot(userId, snapshotName) {
  if (userId == null || String(userId).trim() === '') return null;
  const k = storageKey(userId, snapshotName);
  const rec = await get(k, customStore);
  if (!rec || rec.schemaVersion !== TA_OFFLINE_SNAPSHOT_VERSION) return null;
  if (isExpired(rec)) {
    await del(k, customStore);
    return null;
  }
  return rec;
}

/**
 * @param {string|number} userId
 * @param {string} snapshotName
 * @param {unknown} data
 * @param {number} [ttlMs]
 */
export async function setTASnapshot(userId, snapshotName, data, ttlMs = DEFAULT_TTL_MS) {
  const now = Date.now();
  const rec = {
    userId: String(userId),
    savedAt: now,
    expiresAt: now + (ttlMs == null || Number.isNaN(ttlMs) ? DEFAULT_TTL_MS : ttlMs),
    schemaVersion: TA_OFFLINE_SNAPSHOT_VERSION,
    data,
  };
  await set(storageKey(userId, snapshotName), rec, customStore);
}

/**
 * @param {string|number} userId
 * @param {string} snapshotName
 */
export async function deleteTASnapshot(userId, snapshotName) {
  await del(storageKey(userId, snapshotName), customStore);
}

/**
 * @param {string|number} userId
 */
export async function clearTAOfflineSnapshots(userId) {
  if (userId == null || String(userId).trim() === '') return;
  const prefix = `${NS}:${String(userId)}:`;
  const all = await keys(customStore);
  for (const k of all) {
    if (String(k).startsWith(prefix)) {
      // eslint-disable-next-line no-await-in-loop
      await del(k, customStore);
    }
  }
}

export async function clearAllTAOfflineSnapshots() {
  const all = await keys(customStore);
  for (const k of all) {
    if (String(k).startsWith(`${NS}:`)) {
      // eslint-disable-next-line no-await-in-loop
      await del(k, customStore);
    }
  }
}
